/// <reference types="vite-plugin-pwa/vanillajs" />
import type { Game } from './core/game';
import { mountUpdateNotice } from './ui/updateNotice';

/**
 * Service worker (PWA complète, A44) — enregistrement et mises à jour.
 *
 * Le service worker est généré au build par `vite-plugin-pwa` (Workbox,
 * stratégie `generateSW`, `vite.config.ts`) : il **précache toute
 * l'application** (HTML, JS, CSS, polices, manifeste, icônes, assets de
 * `public/`), le jeu se relance donc hors ligne après la première visite.
 *
 * Mode `prompt` : une nouvelle version s'installe en arrière-plan puis attend ;
 * l'avis « Nouvelle version disponible · Recharger » (`ui/updateNotice.ts`)
 * laisse le joueur choisir le moment. Aucun rechargement forcé.
 *
 * **Production seulement** : en développement (`npm run dev`, e2e Playwright)
 * rien n'est enregistré, et le module virtuel du plugin n'est même pas chargé
 * (import dynamique sous la garde `import.meta.env.PROD`).
 */

/** Vérification périodique d'une nouvelle version, pour une app laissée ouverte. */
const UPDATE_CHECK_MS = 60 * 60 * 1000;
/** Filet de sécurité : si le service worker ne recharge pas la page, on le fait. */
const RELOAD_FALLBACK_MS = 4000;

export function setupPwa(game: Game, beforeReload: () => void): void {
  if (!import.meta.env.PROD) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  void import('virtual:pwa-register')
    .then(({ registerSW }) => {
      const reload = (): void => {
        try {
          beforeReload();
        } catch {
          /* sauvegarde impossible : on recharge quand même, à la demande du joueur */
        }
        window.location.reload();
      };

      let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;
      const notice = mountUpdateNotice({
        phase: game.state.phase,
        activate: () => {
          try {
            beforeReload();
          } catch {
            /* idem */
          }
          // Le service worker en attente prend la main ; le plugin recharge alors
          // la page via `onNeedReload` (événement `controlling`) → `reload`.
          void updateSW?.();
          window.setTimeout(() => window.location.reload(), RELOAD_FALLBACK_MS);
        },
        reload,
      });
      game.on('phase', ({ phase }) => notice.dispatch({ type: 'phase', phase }));

      updateSW = registerSW({
        immediate: true,
        onNeedRefresh: () => notice.dispatch({ type: 'need-refresh' }),
        onNeedReload: () => notice.dispatch({ type: 'controlling' }),
        onRegisteredSW: (_url, registration) => {
          if (!registration) return;
          window.setInterval(() => {
            if (navigator.onLine && !registration.installing) void registration.update().catch(() => undefined);
          }, UPDATE_CHECK_MS);
        },
        onRegisterError: () => {
          /* navigation privée, contexte non sécurisé… : le jeu marche en ligne */
        },
      });
    })
    .catch(() => {
      /* module absent : pas de hors ligne, le jeu fonctionne comme avant */
    });
}
